/**
 * The Snowplow suppression denylist — app POLICY, registered into the pipeline.
 *
 * Event names whose Snowplow send is suppressed. These are high-volume events
 * not consumed in Databricks/Snowplow, so their Snowplow rows are pure
 * ingestion + warehouse cost.
 *
 * The list crosses into `@heidi/runtime/analytics` as config
 * (`registerSnowplowSuppressedEvents`, called from `./wiring.ts`) and is
 * applied in `enrich()`, the one place every emit passes through. It used to
 * be applied inside `trackV2`, one of two app facades — so which entry point a
 * call site imported decided whether the policy applied at all. When that was
 * measured, 16 call sites across 15 files reached the pipeline through
 * `track()` and bypassed the denylist entirely, including the toast-layer
 * `error_viewed` auto-fire named below, which is the surface this list was
 * written for; 4 sites honoured it.
 *
 * WHY THESE TWO. Both rationales below are quoted from the denylist's original
 * docblock, which is their only provenance in this repo — no warehouse or
 * Snowplow console was re-queried when the list moved here, so treat them as
 * that docblock's claim rather than as a current measurement:
 *
 * - `error_viewed` (~3.9M events/7d, ~6% of all frontend Snowplow volume)
 *   auto-fires from the toast layer on every destructive/warning toast — almost
 *   entirely `error_type: unknown` toast noise (401 retry storms, "Speaker is
 *   empty" recording ticks). Queried in Amplitude, not Snowplow.
 * - `screen_viewed` (~2.8M/7d) — no evidence of Snowplow/Databricks usage.
 *
 * Suppression is SNOWPLOW-ONLY: the call site's existing Amplitude/Datadog
 * routing is left untouched (we do NOT add Amplitude). A dispatch authored
 * Snowplow-first (`amplitude: false`, e.g. the session-list `screen_viewed`
 * impressions from MUL-2372) therefore lands Datadog-only — acceptable while
 * unused in Snowplow. Keyed by event NAME so every call site is covered at once
 * and no call site can forget it. A reviewed call site may opt in explicitly
 * with `snowplow: true`; pair that with `amplitude: false` when Snowplow
 * forwarding is the sole Amplitude route.
 */
import { TrackingEventName } from '@/models/analytics/eventTypes';

export const SNOWPLOW_SUPPRESSED_EVENTS: readonly TrackingEventName[] = [
  TrackingEventName.ErrorViewed,
  TrackingEventName.ScreenViewed,
];

const SUPPRESSED_EVENT_NAMES: ReadonlySet<string> = new Set(SNOWPLOW_SUPPRESSED_EVENTS);

/**
 * Whether an event has its Snowplow send suppressed. The app-side reader of the
 * same list the pipeline is registered with — used by the parity harness to
 * derive the expected Snowplow leg without duplicating the denylist.
 */
export function isSnowplowSuppressedEvent(eventName: string): boolean {
  return SUPPRESSED_EVENT_NAMES.has(eventName);
}
